import React, { useMemo } from 'react';
import {
  WxbButton,
  WxbEmpty,
  WxbTag,
} from '../wxb-ui';
import type { ResourceNode, ResourceRelationship } from '../../types/prodResourceTopology';
import {
  RELATIONSHIP_TYPE_LABELS,
  RESOURCE_KIND_LABELS,
} from './resourceTopologyModel';

interface ResourceRelationshipGraphProps {
  resources: ResourceNode[];
  relationships: ResourceRelationship[];
  selectedResourceId: string | null;
  selectedRelationshipId: string | null;
  onSelectResource: (resourceId: string) => void;
  onSelectRelationship: (relationshipId: string) => void;
}

interface Connection {
  relation: ResourceRelationship;
  node: ResourceNode;
}

const kindColor = (node: ResourceNode): 'blue' | 'cyan' | 'amber' | 'neutral' => {
  if (node.kind === 'CIP_LOOP') return 'cyan';
  if (node.kind === 'CIP_STATION') return 'amber';
  if (node.kind === 'EQUIPMENT' || node.kind === 'COMPONENT') return 'blue';
  return 'neutral';
};

const ResourceCard: React.FC<{
  node: ResourceNode;
  selected?: boolean;
  onClick: () => void;
}> = ({ node, selected = false, onClick }) => (
  <WxbButton
    type="button"
    variant="ghost"
    className={`resource-topology-graph-node ${selected ? 'is-selected' : ''}`}
    onClick={onClick}
    aria-pressed={selected}
  >
    <span className="resource-topology-graph-node-heading">
      <span className="resource-topology-graph-node-code">{node.code}</span>
      <WxbTag color={kindColor(node)}>{RESOURCE_KIND_LABELS[node.kind]}</WxbTag>
    </span>
    <span className="resource-topology-graph-node-name">{node.name}</span>
    {node.lifecycleStatus !== 'ACTIVE' && (
      <WxbTag color={node.lifecycleStatus === 'RETIRED' ? 'red' : 'neutral'}>
        {node.lifecycleStatus === 'RETIRED' ? '已退役' : '停用'}
      </WxbTag>
    )}
  </WxbButton>
);

const ConnectionList: React.FC<{
  title: string;
  connections: Connection[];
  relationPosition: 'before' | 'after';
  selectedRelationshipId: string | null;
  onSelectResource: (resourceId: string) => void;
  onSelectRelationship: (relationshipId: string) => void;
}> = ({
  title,
  connections,
  relationPosition,
  selectedRelationshipId,
  onSelectResource,
  onSelectRelationship,
}) => (
  <section className="resource-topology-graph-column" aria-label={title}>
    <h3 className="resource-topology-graph-column-title">{title}</h3>
    <div className="resource-topology-graph-connections">
      {connections.length === 0 ? (
        <span className="resource-topology-graph-placeholder">无关系</span>
      ) : connections.map(({ relation, node }) => (
        <div className="resource-topology-graph-connection" key={relation.id}>
          {relationPosition === 'before' && (
            <WxbButton
              type="button"
              variant="ghost"
              size="sm"
              className={`resource-topology-edge-button ${selectedRelationshipId === relation.id ? 'is-selected' : ''}`}
              onClick={() => onSelectRelationship(relation.id)}
              aria-pressed={selectedRelationshipId === relation.id}
            >
              {RELATIONSHIP_TYPE_LABELS[relation.type]}
              {relation.type === 'CIP_BACKUP_STATION' && relation.priority != null
                ? ` · 优先级 ${relation.priority}`
                : ''}
            </WxbButton>
          )}
          <ResourceCard node={node} onClick={() => onSelectResource(node.id)} />
          {relationPosition === 'after' && (
            <WxbButton
              type="button"
              variant="ghost"
              size="sm"
              className={`resource-topology-edge-button ${selectedRelationshipId === relation.id ? 'is-selected' : ''}`}
              onClick={() => onSelectRelationship(relation.id)}
              aria-pressed={selectedRelationshipId === relation.id}
            >
              {RELATIONSHIP_TYPE_LABELS[relation.type]}
              {relation.type === 'CIP_BACKUP_STATION' && relation.priority != null
                ? ` · 优先级 ${relation.priority}`
                : ''}
            </WxbButton>
          )}
        </div>
      ))}
    </div>
  </section>
);

const ResourceRelationshipGraph: React.FC<ResourceRelationshipGraphProps> = ({
  resources,
  relationships,
  selectedResourceId,
  selectedRelationshipId,
  onSelectResource,
  onSelectRelationship,
}) => {
  const resourceById = useMemo(
    () => new Map(resources.map((resource) => [resource.id, resource])),
    [resources],
  );
  const selectedResource = selectedResourceId ? resourceById.get(selectedResourceId) ?? null : null;
  const incoming = useMemo<Connection[]>(() => {
    if (!selectedResourceId) return [];
    return relationships
      .filter((relation) => relation.targetResourceId === selectedResourceId)
      .map((relation) => ({ relation, node: resourceById.get(relation.sourceResourceId) }))
      .filter((item): item is Connection => Boolean(item.node));
  }, [relationships, resourceById, selectedResourceId]);
  const outgoing = useMemo<Connection[]>(() => {
    if (!selectedResourceId) return [];
    return relationships
      .filter((relation) => relation.sourceResourceId === selectedResourceId)
      .map((relation) => ({ relation, node: resourceById.get(relation.targetResourceId) }))
      .filter((item): item is Connection => Boolean(item.node));
  }, [relationships, resourceById, selectedResourceId]);

  if (!selectedResource) {
    return <WxbEmpty description="从左侧资源树选择节点后查看关系" />;
  }

  return (
    <div className="resource-topology-graph" role="group" aria-label={`${selectedResource.name} 的资源关系`}>
      <ConnectionList
        title="指向当前节点"
        connections={incoming}
        relationPosition="after"
        selectedRelationshipId={selectedRelationshipId}
        onSelectResource={onSelectResource}
        onSelectRelationship={onSelectRelationship}
      />
      <section className="resource-topology-graph-column resource-topology-graph-focus" aria-label="当前节点">
        <h3 className="resource-topology-graph-column-title">当前节点</h3>
        <ResourceCard node={selectedResource} selected onClick={() => onSelectResource(selectedResource.id)} />
      </section>
      <ConnectionList
        title="从当前节点发出"
        connections={outgoing}
        relationPosition="before"
        selectedRelationshipId={selectedRelationshipId}
        onSelectResource={onSelectResource}
        onSelectRelationship={onSelectRelationship}
      />
    </div>
  );
};

export default ResourceRelationshipGraph;
